<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router/composables'
import { useInfiniteScroll } from '@vueuse/core'

import { searchConfig as rocnovelConfig } from './configs/rocnovel'




















import SearchPageDefault from './components/SearchPage.default.vue'



import { fetchSearchResult } from './services/searchApi'
import { bookParams } from '@/composables/tracks/bookParameters'
import { superstoryTrack } from '@/track'
import { brand, features } from '@/config/brands'
import { useHistoryStore, useQueryStore, useToastStore, useTrackValueStore } from '@/stores'
import { READER_ROUTE_NAME } from '@/utils/readerRouteName'
import { TRACK_CHANNEL_NAME_VALUE } from '@/constants'
import { searchTrack } from '@/composables/tracks'
import { loadBrandComponent, loadBrandConfig } from '@/utils/loadBrandConfig'

// 🔥 加载当前品牌配置
const config = loadBrandConfig({ rocnovel: rocnovelConfig })

const SearchPage = loadBrandComponent({ rocnovel: SearchPageDefault })

const { $toast } = useToastStore()
const input = ref('')

const enterSearchTime = ref<number>(Date.now())

const route = useRoute()
const router = useRouter()
const bookshelfSearch = computed(() => !!features?.enableBookshelfSearch && route.query.scope === 'bookshelf')
const fromLibrary = computed(() => !!features?.enableBookshelfSearch && route.query.from === 'library')
onMounted(() => {
  if (route.query.kw) {
    input.value = route.query.kw as string
    onSearch()
  }

  if (route.query.st)
    enterSearchTime.value = Number(route.query.st)
})

const skeleton = ref(false)
const fetching = ref(false)
const ending = ref(false)
const error = ref<any>()
const page = ref(1)
const pagination = computed(() => ({
  pageIndex: page.value,
  pageSize: 20,
}))

const passcodePID = ref('')
const passcode = ref<any>()
const result = ref<any[]>([])

let searchGeneration = 0
let completedKeyword = ''
let activeKeyword = ''
function onClear() {
  input.value = ''
  if (brand === 'superstory') {
    searchGeneration++
    activeKeyword = ''
    result.value = []
    passcode.value = undefined
    passcodePID.value = ''
    error.value = null
    skeleton.value = false
    fetching.value = false
    ending.value = true
  }
}

async function onSearch() {
  if (brand === 'superstory') {
    if (!input.value.trim())
      return
    searchGeneration++
    activeKeyword = input.value.trim()
    passcode.value = undefined
    passcodePID.value = ''
  }
  searchTrack({
    input: input.value,
    currentPage: route.name as string,
    currentPageOpenTime: String(enterSearchTime.value),
    resultPageOpenTime: String(Date.now()),
    duration: String(Date.now() - enterSearchTime.value),
  })

  enterSearchTime.value = Date.now()
  skeleton.value = true
  ending.value = false
  error.value = null
  page.value = 1
  result.value = []
  fetchResult()
}

async function fetchResult() {
  if (brand === 'superstory' && !activeKeyword)
    return
  const request = searchGeneration
  const keyword = brand === 'superstory' ? activeKeyword : input.value
  const requestedPage = page.value
  const started = Date.now()
  fetching.value = true
  try {
    const { data } = await fetchSearchResult(keyword, requestedPage, pagination.value.pageSize, bookshelfSearch.value)
    if (brand === 'superstory' && request !== searchGeneration)
      return
    completedKeyword = keyword
    const duration = Date.now() - started
    if (brand === 'superstory') {
      const records = data.nameSearchResult?.records || []
      if (requestedPage === 1)
        superstoryTrack('search_result_page', { input: keyword, is_result_empty: !records.length && !data.codeSearchResult, source: route.query.from || 'discover' })
      records.forEach((item: any, index: number) => {
        item.pos = result.value.length + index + 1
        item.tracking = { keyword, result_count: data.nameSearchResult?.total ?? records.length, duration_ms: duration, source: route.query.from || 'discover' }
      })
    }
    passcode.value = data.codeSearchResult
    passcodePID.value = data.promotionId ?? ''

    if (!data.nameSearchResult) {
      ending.value = true
      return
    }

    result.value = [...result.value, ...(data.nameSearchResult.records ?? [])]
    if (page.value >= data.nameSearchResult.pages)
      ending.value = true
    else
      page.value += 1
  }
  catch (_error: any) {
    if (brand === 'superstory' && request !== searchGeneration)
      return
    console.error(_error)
    error.value = _error
    $toast(_error.msg || '服务器错误')
  }
  finally {
    if (brand !== 'superstory' || request === searchGeneration) {
      fetching.value = false
      skeleton.value = false
    }
  }
}

const infiniteRef = ref<HTMLElement>()
function setInfiniteRef(el: HTMLElement | null) {
  infiniteRef.value = el || undefined
}
useInfiniteScroll(infiniteRef,
  async () => {
    if (fetching.value || ending.value || error.value)
      return
    await fetchResult()
  },
  { distance: 50 },
)

const queryStore = useQueryStore()
const historyStore = useHistoryStore()
async function gotoPasscodeBook(passcode: any) {
  if (passcodePID.value) // Note: 如果存在 promotionId, 需要更新全局 `max_pid`
    queryStore.$state.max_pid = passcodePID.value

  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId: passcode.id, firstOrigin: 3, originName: 3, chlName: TRACK_CHANNEL_NAME_VALUE.SEARCH_LIST })
  // Note: clear custom history stack, avoid back to old `max_pid`
  historyStore.$clear()
  router.replace({ name: READER_ROUTE_NAME, params: { contentId: passcode.id, chapterId: passcode.firstChapter?.id || '' } })
}

function onTrackGotoBook(bookId: string) {
  const index = result.value.findIndex(item => item.id === bookId)
  if (index >= 0)
    superstoryTrack('search_result_ck', { ...bookParams(result.value[index], index + 1), keyword: completedKeyword, source: route.query.from || 'discover' })
  const trackValueStore = useTrackValueStore()
  trackValueStore.set({ bookId, firstOrigin: 3, originName: 3, chlName: TRACK_CHANNEL_NAME_VALUE.SEARCH_LIST })
  return true
}

const gotoBack = function () {
  if (fromLibrary.value) {
    router.replace({ name: 'history', query: { tab: route.query.tab === 'history' ? 'history' : 'library' } })
    return
  }
  historyStore.$clear()
  router.replace({ name: 'home' })
}

onBeforeRouteLeave((to, from, next) => {
  if (brand === 'superstory') {
    searchGeneration++
    next()
    return
  }
  searchTrack({
    input: input.value,
    currentPage: to.path as string,
    currentPageOpenTime: String(enterSearchTime.value),
    resultPageOpenTime: String(Date.now()),
    duration: String(Date.now() - enterSearchTime.value),
  })
  next()
})
</script>

<template>
  <SearchPage
    :config="config"
    :input="input"
    :skeleton="skeleton"
    :fetching="fetching"
    :ending="ending"
    :error="error"
    :passcode="passcode"
    :result="result"
    :set-infinite-ref="setInfiniteRef"
    :on-clear="onClear"
    :on-search="onSearch"
    :goto-back="gotoBack"
    :goto-passcode-book="gotoPasscodeBook"
    :on-track-goto-book="onTrackGotoBook"
    @update:input="input = $event"
  />
</template>
